const DEFAULT_LANGUAGE = 'br'
const STORAGE_KEY = 'interface-preferences--language'

const API_TO_LOCALE = {
  'pt-BR': 'br',
  'pt-br': 'br',
  pt_BR: 'br',
  pt: 'br',
  br: 'br',
  'en-US': 'en',
  'en-us': 'en',
  en_US: 'en',
  en: 'en',
  'es-AR': 'es',
  'es-ar': 'es',
  es_AR: 'es',
  es: 'es',
}

const STORAGE_TO_LOCALE = {
  pt: 'br',
  br: 'br',
  en: 'en',
  es: 'es',
}

const LOCALE_TO_STORAGE = {
  br: 'pt',
  en: 'en',
  es: 'es',
}

const LOCALE_TO_API = {
  br: 'pt-BR',
  en: 'en-US',
  es: 'es-AR',
}

const LOCALE_TO_VUETIFY = {
  br: 'pt',
  en: 'en',
  es: 'es',
}

// Os arquivos vivem em src/assets/ e chegam à raiz web pelo
// CopyWebpackPlugin({ from: './src/assets/', to: './' }) do vue.config.js —
// por isso as URLs de raiz resolvem (conferível em dist/ após o build).
const TEMPLATE_URL_BY_LOCALE = {
  br: '/modelo.xlsx',
  en: '/modelo-en.xlsx',
  es: '/modelo-es.xlsx',
}

// Template do fluxo LEGADO (plano híbrido §4, D-H6): o modelo.xlsx da
// develop restaurado como modelo-legado.xlsx — sempre pt, como era na develop
// (não existe versão en/es do modelo antigo).
// Já NÃO é byte-idêntico à develop: por decisão do CTO em 2026-10-02 a linha de
// instruções dele também virou comentário de célula, porque o controller lê os
// DOIS fluxos com `rowTitleHeaderPosition = 1` e a linha 2 era lida como dado
// (ver scripts/regenerate-template-instructions.js).
const LEGACY_TEMPLATE_URL = '/modelo-legado.xlsx'

export function calcLanguageToLocale(lang) {
  if (!lang) return DEFAULT_LANGUAGE
  return (
    API_TO_LOCALE[lang] ||
    API_TO_LOCALE[String(lang).toLowerCase()] ||
    DEFAULT_LANGUAGE
  )
}

export function calcLocaleToApiLanguage(locale) {
  return LOCALE_TO_API[calcLanguageToLocale(locale)]
}

export function calcLocaleToVuetifyLang(locale) {
  return LOCALE_TO_VUETIFY[calcLanguageToLocale(locale)]
}

export function calcTemplateUrlByLocale(language) {
  return (
    TEMPLATE_URL_BY_LOCALE[calcLanguageToLocale(language)] ||
    TEMPLATE_URL_BY_LOCALE[DEFAULT_LANGUAGE]
  )
}

// Template por FLUXO (plano híbrido §4): opção legada → modelo-legado.xlsx
// (sempre pt, como na develop); opção "(Novo)" → modelo por locale do branch.
export function calcTemplateUrlByFlow(flow, language) {
  if (flow === 'legacy') return LEGACY_TEMPLATE_URL
  return calcTemplateUrlByLocale(language)
}

export function getStoredLanguage() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored ? STORAGE_TO_LOCALE[stored] || null : null
  } catch (e) {
    return null
  }
}

export function setStoredLanguage(locale) {
  try {
    const storageValue = LOCALE_TO_STORAGE[locale] || locale
    localStorage.setItem(STORAGE_KEY, storageValue)
  } catch (e) {
    /* ignore quota / disabled storage */
  }
}

export function getNavigatorLanguage() {
  const nav =
    (typeof navigator !== 'undefined' &&
      (navigator.language || navigator.userLanguage)) ||
    ''
  return calcLanguageToLocale(nav)
}

export function decodeJwtPayload(token) {
  if (!token || typeof token !== 'string') return null
  const parts = token.split('.')
  if (parts.length < 2) return null
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64 + '==='.slice((base64.length + 3) % 4)
    const json = decodeURIComponent(
      atob(padded)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    return JSON.parse(json)
  } catch (e) {
    return null
  }
}

export function extractLanguageFromLegacyToken(token) {
  const payload = decodeJwtPayload(token)
  if (!payload) return null
  const lang = payload.language || payload.lang || payload.locale
  return lang ? calcLanguageToLocale(lang) : null
}

export async function extractLanguageFromFirebaseUser(user) {
  if (!user || typeof user.getIdTokenResult !== 'function') return null
  try {
    const result = await user.getIdTokenResult()
    const claims = (result && result.claims) || {}
    const lang = claims.language || claims.lang || claims.locale
    return lang ? calcLanguageToLocale(lang) : null
  } catch (e) {
    return null
  }
}

export function applyLanguage(i18n, locale, vuetify) {
  const normalized = calcLanguageToLocale(locale)
  if (!i18n || !normalized) return null
  if (i18n.locale !== normalized) {
    i18n.locale = normalized
  }
  if (vuetify && vuetify.lang) {
    vuetify.lang.current = calcLocaleToVuetifyLang(normalized)
  }
  setStoredLanguage(normalized)
  return normalized
}

// O storage guarda a escolha explícita mais recente do usuário (feita no
// LanguageMenu ou sincronizada de um login anterior); as claims do custom
// token não mudam durante a sessão, então só servem de fallback — se tivessem
// prioridade, reverteriam a troca de idioma após o reload.
export async function resolveSessionLocale(user) {
  const stored = getStoredLanguage()
  if (stored) return stored
  return extractLanguageFromFirebaseUser(user)
}

export function resolveInitialLocale() {
  return getStoredLanguage() || getNavigatorLanguage() || DEFAULT_LANGUAGE
}

export { DEFAULT_LANGUAGE, STORAGE_KEY, LEGACY_TEMPLATE_URL }
